/**
 * Capacitor Mobile Integration
 *
 * This module provides the core Capacitor functionality for the JD Simplified mobile app.
 *
 * @see docs/mobile-app/CAPACITOR-SETUP.md for setup guide
 * @see docs/mobile-app/DEEP-LINKING.md for deep link configuration
 */

import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Keyboard, KeyboardResize } from '@capacitor/keyboard';
import { Network } from '@capacitor/network';
import { SplashScreen } from '@capacitor/splash-screen';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { logger } from '@/lib/logger';

// Re-export for convenience
export { Capacitor } from '@capacitor/core';
export { Preferences } from '@capacitor/preferences';
export { Browser } from '@capacitor/browser';

// Check if running in native app
export const isNativeApp = Capacitor.isNativePlatform();
export const platform = Capacitor.getPlatform(); // 'ios', 'android', or 'web'

/**
 * Initialize Capacitor plugins and listeners.
 * Call this early in app initialization (main.tsx).
 *
 * NOTE: Deep link handling is separate - call initializeDeepLinks() from App.tsx
 * with access to the router's navigate function.
 */
export async function initializeCapacitor(): Promise<void> {
  if (!isNativeApp) {
    logger.debug('[Capacitor] Not a native platform, skipping initialization');
    return;
  }

  logger.info('[Capacitor] Initializing native plugins...');

  try {
    // Hide splash screen after app is ready
    await SplashScreen.hide();
    logger.debug('[Capacitor] Splash screen hidden');

    // Configure status bar (iOS only)
    if (platform === 'ios') {
      await StatusBar.setStyle({ style: Style.Dark });
      logger.debug('[Capacitor] Status bar configured');
    }

    // Configure keyboard behavior (iOS only — Android throws UNIMPLEMENTED,
    // which must not abort the listener registrations below)
    if (platform === 'ios') {
      await Keyboard.setResizeMode({ mode: KeyboardResize.Body });
      logger.debug('[Capacitor] Keyboard configured');
    }

    // Listen for app state changes (background/foreground)
    App.addListener('appStateChange', ({ isActive }) => {
      logger.info(`[Capacitor] App state changed: ${isActive ? 'active' : 'background'}`);
      if (isActive) {
        // App came to foreground - good place to refresh data
        window.dispatchEvent(new CustomEvent('capacitor:appResumed'));
      } else {
        // App went to background
        window.dispatchEvent(new CustomEvent('capacitor:appPaused'));
      }
    });

    // Listen for back button (Android)
    App.addListener('backButton', ({ canGoBack }) => {
      logger.debug(`[Capacitor] Back button pressed, canGoBack: ${canGoBack}`);
      if (!canGoBack) {
        // At root of navigation - exit app
        App.exitApp();
      } else {
        // Go back in history
        window.history.back();
      }
    });

    // Monitor network status
    const networkStatus = await Network.getStatus();
    logger.info(`[Capacitor] Initial network status: ${networkStatus.connected ? 'online' : 'offline'}`);

    Network.addListener('networkStatusChange', (status) => {
      logger.info(`[Capacitor] Network status changed: ${status.connected ? 'online' : 'offline'}`);
      window.dispatchEvent(new CustomEvent('capacitor:networkChange', {
        detail: { connected: status.connected, connectionType: status.connectionType }
      }));
    });

    logger.info('[Capacitor] Initialization complete');
  } catch (error) {
    logger.error('[Capacitor] Initialization error:', error);
    // Don't throw - allow app to continue even if some plugins fail
  }
}

/**
 * Haptic feedback helper
 */
export async function hapticFeedback(style: 'light' | 'medium' | 'heavy' = 'medium'): Promise<void> {
  if (!isNativeApp) return;

  const impactStyle = {
    light: ImpactStyle.Light,
    medium: ImpactStyle.Medium,
    heavy: ImpactStyle.Heavy
  }[style];

  try {
    await Haptics.impact({ style: impactStyle });
  } catch (error) {
    logger.warn('[Capacitor] Haptic feedback failed:', error);
  }
}

/**
 * Check if the app is currently online
 */
export async function isOnline(): Promise<boolean> {
  if (!isNativeApp) {
    return navigator.onLine;
  }

  const status = await Network.getStatus();
  return status.connected;
}
